import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import Layout from "@/components/Layout";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  ArrowLeft,
  Save,
  Building2,
  Upload,
  Sparkles,
  CheckCircle,
  AlertTriangle,
  IndianRupee,
  Phone,
  MapPin,
  User,
  Shield,
  Info,
  Trash2,
  FileType,
  Image,
  Package,
  BriefcaseBusiness,
} from "lucide-react";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Settings() {
  const navigate = useNavigate();
  const user = JSON.parse(localStorage.getItem("user") || "{}");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [logoPreview, setLogoPreview] = useState(null);
  const [formData, setFormData] = useState({
    company_name: "",
    company_gst_no: "",
    company_type: "",
    company_logo: "",
    address: "",
    contact_person: "",
    contact_number: "",
  });

  useEffect(() => {
    if (user.account_type === "team_member") {
      toast.error("CA employees cannot change company settings");
      navigate("/");
      return;
    }
    loadSettings();
  }, [navigate, user.account_type]);

  const loadSettings = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/settings/company`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.data && response.data.company_name) {
        setFormData((prev) => ({
          ...prev,
          ...response.data,
          company_type: response.data.company_type || "",
        }));
        if (response.data.company_logo) {
          setLogoPreview(response.data.company_logo);
        }
      }
      setLoading(false);
    } catch (error) {
      toast.error("Failed to load settings");
      setLoading(false);
    }
  };

  const handleLogoUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      toast.error("Please upload an image file");
      return;
    }

    if (file.type !== "image/png") {
      toast.warning("PNG format recommended for transparent backgrounds");
    }

    if (file.size > 2 * 1024 * 1024) {
      toast.error("Logo size must be less than 2MB");
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      const base64String = reader.result;
      setLogoPreview(base64String);
      setFormData({ ...formData, company_logo: base64String });
      toast.success("Logo uploaded successfully!");
    };
    reader.readAsDataURL(file);
  };

  const handleSave = async (e) => {
    e.preventDefault();

    if (!formData.company_name || !formData.company_gst_no || !formData.company_type) {
      toast.error("Company name, GST number and company type are required");
      return;
    }

    setSaving(true);
    try {
      const token = localStorage.getItem("token");
      await axios.post(`${API}/settings/company`, formData, {
        headers: { Authorization: `Bearer ${token}` },
      });
      // toast.success("Settings saved successfully!");
      location.reload();
    } catch (error) {
      toast.error("Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  const removeLogo = () => {
    setLogoPreview(null);
    setFormData({ ...formData, company_logo: "" });
    toast.success("Logo removed");
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex flex-col items-center justify-center min-h-[300px] space-y-3">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
          <p className="text-gray-500 text-sm">Loading company settings...</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-7xl mx-auto space-y-4" data-testid="settings-page">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-2">
          <div>
            <h1 className="text-lg font-bold bg-gradient-to-r from-primary-500 to-orange-500 bg-clip-text text-transparent animate-gradient">
              Company Settings
            </h1>
            <p className="text-gray-500 text-xs mt-0.5">
              Configure your company details for GST verification
            </p>
          </div>
          <div className="flex gap-2">
            <Badge className="text-xs py-0.5 px-2.5 bg-gradient-to-r from-primary-500 to-primary-600 text-white shadow-sm">
              <Sparkles size={10} className="mr-1" />
              GST Verified
            </Badge>
            <Badge className="text-xs py-0.5 px-2.5 bg-gradient-to-r from-orange-500 to-orange-600 text-white shadow-sm">
              <Shield size={10} className="mr-1" />
              Secure
            </Badge>
          </div>
        </div>

        {/* Info Alert */}
        <Alert className="bg-gradient-to-r from-primary-50/80 to-orange-50/60 border border-primary-200/70 backdrop-blur-sm">
          <div className="flex items-start gap-2.5">
            <div className="p-1.5 rounded-md bg-gradient-to-br from-primary-400 to-primary-500">
              <Info size={14} className="text-white" />
            </div>
            <div>
              <AlertTitle className="text-sm font-bold text-gray-800">
                Important Information
              </AlertTitle>
              <AlertDescription className="text-xs text-gray-600 mt-1">
                Your company GST number will be used to validate invoices:
                <ul className="list-disc ml-4 mt-1 space-y-1">
                  <li>
                    <strong className="text-gray-700">
                      Purchase Invoices:
                    </strong>{" "}
                    Bill To GST must match your company GST
                  </li>
                  <li>
                    <strong className="text-gray-700">Sales Invoices:</strong>{" "}
                    Bill From GST must match your company GST
                  </li>
                </ul>
              </AlertDescription>
            </div>
          </div>
        </Alert>

        {/* Settings Form */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* Left Column - Logo and Summary */}
          <div className="lg:col-span-1 space-y-4">
            <Card className="border-0 bg-gradient-to-br from-white to-gray-50 shadow-sm">
              <CardHeader className="pb-3 border-b border-gray-100">
                <div className="flex items-center justify-between">
                  <div className="space-y-1">
                    <CardTitle className="text-sm font-bold text-gray-800 flex items-center gap-2">
                      <div className="p-1.5 rounded-lg bg-gradient-to-br from-primary-400 to-primary-500">
                        <Building2 size={14} className="text-white" />
                      </div>
                      Company Logo
                    </CardTitle>
                    <CardDescription className="text-xs text-gray-500">
                      Upload your company logo (PNG recommended)
                    </CardDescription>
                  </div>
                  <Badge variant="outline" className="text-xs bg-white">
                    <Upload size={10} className="mr-1" />
                    Upload
                  </Badge>
                </div>
              </CardHeader>
              <CardContent className="pt-4">
                <div className="space-y-4">
                  {logoPreview ? (
                    <div className="space-y-3">
                      <div className="w-full aspect-square border-2 border-dashed border-gray-200 rounded-xl overflow-hidden flex items-center justify-center bg-gradient-to-br from-gray-50 to-white p-3">
                        <img
                          src={logoPreview}
                          alt="Company Logo Preview"
                          className="w-full h-full object-contain"
                        />
                      </div>
                      <div className="flex gap-2">
                        <input
                          type="file"
                          id="logo-upload"
                          accept="image/png,image/jpeg,image/jpg"
                          onChange={handleLogoUpload}
                          className="hidden"
                          data-testid="logo-upload-input"
                        />
                        <Button
                          variant="outline"
                          onClick={() =>
                            document.getElementById("logo-upload")?.click()
                          }
                          className="flex-1 h-7 text-xs border-gray-300 hover:border-primary-400 hover:bg-primary-50"
                        >
                          <Upload size={10} className="mr-1.5" />
                          Change
                        </Button>
                        <Button
                          variant="outline"
                          onClick={removeLogo}
                          className="flex-1 h-7 text-xs border-red-200 text-red-600 hover:text-red-700 hover:bg-red-50 hover:border-red-300"
                        >
                          <Trash2 size={10} className="mr-1.5" />
                          Remove
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div
                      className="relative border-2 border-dashed rounded-xl p-6 text-center cursor-pointer transition-all duration-300 border-primary-300 hover:border-primary-500 bg-gradient-to-br from-primary-50/30 to-primary-100/20"
                      onClick={() =>
                        document.getElementById("logo-upload")?.click()
                      }
                    >
                      <input
                        type="file"
                        id="logo-upload"
                        accept="image/png,image/jpeg,image/jpg"
                        onChange={handleLogoUpload}
                        className="hidden"
                        data-testid="logo-upload-input"
                      />

                      <div className="space-y-4">
                        <div className="flex justify-center">
                          <div className="p-3 rounded-full bg-gradient-to-br from-primary-50 to-orange-50">
                            <Upload className="text-primary-500" size={22} />
                          </div>
                        </div>
                        <div>
                          <p className="text-sm font-bold text-gray-800">
                            Click to upload logo
                          </p>
                          <p className="text-xs text-gray-500 mt-1">
                            or drag and drop PNG, JPG
                          </p>
                        </div>
                        <div className="pt-2">
                          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white border border-gray-200">
                            <div className="flex items-center gap-1 text-xs text-gray-600">
                              <Image size={10} />
                              <span>Max 2MB</span>
                            </div>
                            <div className="w-1 h-1 rounded-full bg-gray-300"></div>
                            <div className="flex items-center gap-1 text-xs text-gray-600">
                              <FileType size={10} />
                              <span>Square format</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  <Separator />

                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <div className="p-1 rounded-md bg-gradient-to-br from-green-100 to-emerald-100">
                        <Info size={12} className="text-green-600" />
                      </div>
                      <h4 className="font-medium text-xs text-gray-700">
                        Logo Guidelines
                      </h4>
                    </div>
                    <ul className="space-y-1.5">
                      {[
                        "PNG recommended for transparent background",
                        "Square format (1:1 aspect ratio)",
                        "Max file size: 2MB",
                        "Recommended size: 500×500px",
                      ].map((guideline, idx) => (
                        <li key={idx} className="flex items-start gap-2">
                          <CheckCircle
                            size={10}
                            className="text-green-500 mt-0.5 flex-shrink-0"
                          />
                          <span className="text-[10px] text-gray-600">
                            {guideline}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Right Column - Form */}
          <div className="lg:col-span-2">
            <Card className="border-0 bg-gradient-to-br from-white to-gray-50 shadow-sm">
              <CardHeader className="pb-3 border-b border-gray-100">
                <div className="flex items-center justify-between">
                  <div className="space-y-1">
                    <CardTitle className="text-sm font-bold text-gray-800 flex items-center gap-2">
                      <div className="p-1.5 rounded-lg bg-gradient-to-br from-primary-400 to-primary-500">
                        <Building2 size={14} className="text-white" />
                      </div>
                      Company Information
                    </CardTitle>
                    <CardDescription className="text-xs text-gray-500">
                      Update your company details for invoice processing
                    </CardDescription>
                  </div>
                  <Badge variant="outline" className="text-xs bg-white">
                    <Shield size={10} className="mr-1" />
                    Verified
                  </Badge>
                </div>
              </CardHeader>
              <CardContent className="pt-4">
                <form onSubmit={handleSave} className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {/* Company Name */}
                    <div className="md:col-span-2 space-y-1.5">
                      <Label
                        htmlFor="company_name"
                        className="text-xs font-medium text-gray-700"
                      >
                        <Building2
                          size={12}
                          className="inline mr-1.5 text-gray-500"
                        />
                        Company Name <span className="text-red-500">*</span>
                      </Label>
                      <div className="relative group">
                        <Input
                          id="company_name"
                          data-testid="company-name-input"
                          value={formData.company_name}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              company_name: e.target.value,
                            })
                          }
                          placeholder="SMART ITBOX Pvt Ltd"
                          className="h-8 text-xs pl-9 border-gray-300 focus:border-primary-400 focus:ring-1 focus:ring-primary-400/20 group-hover:border-primary-300"
                          required
                        />
                        <Building2
                          size={14}
                          className="absolute left-2.5 top-1/2 transform -translate-y-1/2 text-gray-400 group-hover:text-primary-500"
                        />
                      </div>
                    </div>

                    {/* GST Number */}
                    <div className="space-y-1.5">
                      <Label
                        htmlFor="company_gst_no"
                        className="text-xs font-medium text-gray-700"
                      >
                        <IndianRupee
                          size={12}
                          className="inline mr-1.5 text-gray-500"
                        />
                        Company GST Number{" "}
                        <span className="text-red-500">*</span>
                      </Label>
                      <div className="relative group">
                        <Input
                          id="company_gst_no"
                          data-testid="company-gst-input"
                          value={formData.company_gst_no}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              company_gst_no: e.target.value.toUpperCase(),
                            })
                          }
                          placeholder="29ABCDE1234F1Z5"
                          className="h-8 text-xs font-mono pl-9 border-gray-300 focus:border-primary-400 focus:ring-1 focus:ring-primary-400/20 group-hover:border-primary-300"
                          maxLength={15}
                          required
                        />
                        <Shield
                          size={14}
                          className="absolute left-2.5 top-1/2 transform -translate-y-1/2 text-gray-400 group-hover:text-primary-500"
                        />
                      </div>
                      <p className="text-[10px] text-gray-500">
                        15-character alphanumeric GST identification number
                      </p>
                    </div>

                    <div className="space-y-1.5">
                      <Label className="text-xs font-medium text-gray-700">
                        <Package
                          size={12}
                          className="inline mr-1.5 text-gray-500"
                        />
                        Company Type <span className="text-red-500">*</span>
                      </Label>
                      <Select
                        value={formData.company_type}
                        onValueChange={(value) =>
                          setFormData({
                            ...formData,
                            company_type: value,
                          })
                        }
                      >
                        <SelectTrigger className="h-8 text-xs border-gray-300 focus:border-primary-400 focus:ring-1 focus:ring-primary-400/20">
                          <SelectValue placeholder="Select company type" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="product_based">
                            Product Based
                          </SelectItem>
                          <SelectItem value="service_based">
                            Service Based
                          </SelectItem>
                        </SelectContent>
                      </Select>
                      <p className="text-[10px] text-gray-500">
                        Product based companies will auto-build item master and inventory from verified invoices.
                      </p>
                    </div>

                    {/* Address */}
                    <div className="md:col-span-2 space-y-1.5">
                      <Label
                        htmlFor="address"
                        className="text-xs font-medium text-gray-700"
                      >
                        <MapPin
                          size={12}
                          className="inline mr-1.5 text-gray-500"
                        />
                        Registered Address
                      </Label>
                      <div className="relative group">
                        <Textarea
                          id="address"
                          data-testid="address-input"
                          value={formData.address}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              address: e.target.value,
                            })
                          }
                          placeholder="123 MG Road, Bangalore, Karnataka - 560001"
                          className="text-xs min-h-[70px] pl-9 pt-2 border-gray-300 focus:border-primary-400 focus:ring-1 focus:ring-primary-400/20 group-hover:border-primary-300"
                          rows={2}
                        />
                        <MapPin
                          size={14}
                          className="absolute left-2.5 top-3 text-gray-400 group-hover:text-primary-500"
                        />
                      </div>
                    </div>

                    {/* Contact Person */}
                    <div className="space-y-1.5">
                      <Label
                        htmlFor="contact_person"
                        className="text-xs font-medium text-gray-700"
                      >
                        <User
                          size={12}
                          className="inline mr-1.5 text-gray-500"
                        />
                        Contact Person
                      </Label>
                      <div className="relative group">
                        <Input
                          id="contact_person"
                          data-testid="contact-person-input"
                          value={formData.contact_person}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              contact_person: e.target.value,
                            })
                          }
                          placeholder="John Doe"
                          className="h-8 text-xs pl-9 border-gray-300 focus:border-primary-400 focus:ring-1 focus:ring-primary-400/20 group-hover:border-primary-300"
                        />
                        <User
                          size={14}
                          className="absolute left-2.5 top-1/2 transform -translate-y-1/2 text-gray-400 group-hover:text-primary-500"
                        />
                      </div>
                    </div>

                    {/* Contact Number */}
                    <div className="space-y-1.5">
                      <Label
                        htmlFor="contact_number"
                        className="text-xs font-medium text-gray-700"
                      >
                        <Phone
                          size={12}
                          className="inline mr-1.5 text-gray-500"
                        />
                        Contact Number
                      </Label>
                      <div className="relative group">
                        <Input
                          id="contact_number"
                          data-testid="contact-number-input"
                          value={formData.contact_number}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              contact_number: e.target.value,
                            })
                          }
                          placeholder="+91 9876543210"
                          className="h-8 text-xs pl-9 border-gray-300 focus:border-primary-400 focus:ring-1 focus:ring-primary-400/20 group-hover:border-primary-300"
                        />
                        <Phone
                          size={14}
                          className="absolute left-2.5 top-1/2 transform -translate-y-1/2 text-gray-400 group-hover:text-primary-500"
                        />
                      </div>
                    </div>
                  </div>

                  <Separator />

                  {/* Form Actions */}
                  <div className="flex gap-3 pt-2">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => navigate("/")}
                      className="flex-1 h-8 text-xs border-gray-300 hover:border-gray-400 bg-white"
                    >
                      <ArrowLeft size={12} className="mr-1.5" />
                      Back to Dashboard
                    </Button>
                    <Button
                      type="submit"
                      data-testid="save-settings-btn"
                      disabled={saving}
                      className="flex-1 h-8 text-xs bg-gradient-to-r from-primary-500 to-orange-500 hover:from-primary-600 hover:to-orange-600 text-white shadow-sm"
                    >
                      {saving ? (
                        <>
                          <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white mr-1.5"></div>
                          Saving...
                        </>
                      ) : (
                        <>
                          <Save size={12} className="mr-1.5" />
                          Save
                        </>
                      )}
                    </Button>
                  </div>
                </form>
              </CardContent>
            </Card>

            {/* Quick Tips */}
            <div className="relative overflow-hidden rounded-lg border border-primary-200/50 bg-gradient-to-br from-primary-50/30 to-orange-50/20 mt-3">
              <div className="absolute top-0 right-0 w-16 h-16 bg-gradient-to-br from-primary-500 to-orange-400 opacity-40 rounded-bl-full"></div>

              <div className="p-4 relative">
                <div className="flex items-start gap-3">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <h3 className="text-sm font-semibold bg-gradient-to-r from-primary-600 to-orange-600 bg-clip-text text-transparent">
                        Quick Tips
                      </h3>
                      <Badge
                        variant="outline"
                        className="text-[10px] py-0 px-1.5 border-primary-200 text-primary-600"
                      >
                        Pro Tips
                      </Badge>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      {[
                        {
                          tip: "Keep your GST number updated for invoice validation",

                          color: "from-green-500 to-emerald-500",
                        },
                        {
                          tip:
                            formData.company_type === "product_based"
                              ? "Verified item invoices will update item master and stock automatically"
                              : "Service based companies can focus on GST and invoice compliance without stock posting",

                          color: "from-blue-500 to-cyan-500",
                        },
                        {
                          tip: "Contact information helps in reconciliation",

                          color: "from-purple-500 to-pink-500",
                        },
                        {
                          tip:
                            formData.company_type === "service_based"
                              ? "Choose service based when invoices do not need inventory movement"
                              : "Choose product based when invoices contain item-wise goods movement",

                          color: "from-orange-500 to-amber-500",
                        },
                      ].map((item, index) => (
                        <div
                          key={index}
                          className="flex items-center gap-2 p-2 rounded-md bg-white/50 border border-gray-200/50 hover:bg-white hover:border-primary-200/70 transition-all duration-200"
                        >
                          <p className="text-xs text-gray-700 flex-1">
                            {item.tip}
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}
